<template>
  <div>
    <label
        v-if="props.label"
        class="block text-sm font-medium text-white"
        :for="props.id"
    >{{ props.label }}</label>

    <Combobox v-model="selectedItem" as="div" class="relative mt-2">
      <div class="relative">
        <Icon
          v-if="hasValidSelection"
          name="solar:check-circle-bold"
          size="1.2rem"
          class="absolute left-3 top-1/2 -translate-y-1/2 text-green-500 z-10"
        />
        <ComboboxInput
          :id="props.id"
          class="h-[50px] w-full bg-transparent text-sm focus:ring-primary focus:outline-none border rounded-lg placeholder-gray-500 text-gray-300"
          :class="[
            hasValidSelection ? 'pl-10 pr-8 border-green-500' : 'px-3 border-[#373737]'
          ]"
          :placeholder="props.placeholder"
          :display-value="(item) => item?.label || ''"
          @change="debouncedSearchQuery"
          @focus="onFocus"
        />
      </div>

      <ComboboxButton class="absolute inset-y-0 right-0 flex items-center pr-2">
        <Icon name="solar:alt-arrow-down-linear" size="1.2rem" class="text-gray-500" />
      </ComboboxButton>

      <transition
          enter-active-class="transition duration-100 ease-out"
          enter-from-class="transform scale-95 opacity-0"
          enter-to-class="transform scale-100 opacity-100"
          leave-active-class="transition duration-75 ease-out"
          leave-from-class="transform scale-100 opacity-100"
          leave-to-class="transform scale-95 opacity-0"
      >
        <ComboboxOptions
            v-if="showOptions"
            class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
        >

          <div v-if="props.loading" class="px-4 py-2 text-gray-500 text-center">
            <div class="flex items-center justify-center">
              <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              <span class="ml-2">{{ t('combobox.searching') }}</span>
            </div>
          </div>

          <div v-else-if="filteredItems.length === 0 && hasSearched" class="px-4 py-2 text-gray-500 text-center">
            {{ t('combobox.no_results') }}
          </div>


          <ComboboxOption
              v-for="item in filteredItems"
              :key="item.value"
              :value="item"
              :disabled="item.disabled"
              v-slot="{ active, selected, disabled }"
              data-e2e="combobox-option"
          >
            <li
                :class="[
                  active ? 'bg-blue-100 text-blue-900' : 'text-gray-900',
                  disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                  'relative select-none py-2 pl-10 pr-4'
                ]"
            >
              <div class="flex items-center gap-2">
                <span :class="[selected ? 'font-medium' : 'font-normal', 'truncate']">{{ item.label }}</span>
                <span v-if="item.sublabel" class="text-xs text-gray-500 truncate">{{ item.sublabel }}</span>
              </div>
              <span v-if="selected" class="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                <Icon name="solar:check-circle-bold-duotone" size="1.2rem" />
              </span>
            </li>
          </ComboboxOption>
        </ComboboxOptions>
      </transition>
    </Combobox>

    <transition>
      <div v-if="error" class="text-[#cf5454] bg-[#4d000069] rounded-b-xl px-4 pt-3 pb-1 -mt-3">
        <small>{{ error }}</small>
      </div>
    </transition>
  </div>
</template>

<script lang="ts" setup>
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOptions,
  ComboboxOption,
} from '@headlessui/vue'
import { computed, ref, watch, nextTick, toRef } from 'vue'
import { useField } from "vee-validate"
import { useDebounceFn } from '@vueuse/core'

const { t } = useI18n()

export interface ComboboxItem {
  value: string | number;
  label: string;
  sublabel?: string;
  disabled?: boolean;
}

interface Props {
  items?: ComboboxItem[];
  id?: string;
  label?: string;
  placeholder?: string;
  name?: string;
  modelValue: ComboboxItem | null;
  rules?: string | Record<string, any> | Function;
  minSearchLength?: number;
  debounceMs?: number;
  showOptionsOnFocus?: boolean;
  loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  items: () => [],
  modelValue: null,
  id: () => `combobox-${new Date().getTime().toString()}`,
  minSearchLength: 2,
  debounceMs: 300,
  showOptionsOnFocus: true,
  loading: false,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: ComboboxItem | null): void;
  (e: 'search', query: string): void;
}>()

// Form validation
const nameRef = toRef(props, 'name');
const { value, errorMessage } = useField(nameRef, props.rules, {
  initialValue: props.modelValue
});

const error = computed(() => errorMessage.value);

// Search state
const hasSearched = ref(false)
const debouncedSearchQuery = useDebounceFn((event) => {
  showOptions.value = true

  if (event.target.value.length < props.minSearchLength) {
    hasSearched.value = false
    return
  }

  hasSearched.value = true
  emit('search', event.target.value)
}, 300)
const showOptions = ref(false)

// Computed selected item
const selectedItem = computed({
  get: () => props.modelValue,
  set: (value: ComboboxItem | null) => {
    emit('update:modelValue', value)
  }
})

// Check if there's a valid selection (not null/undefined and has a value)
const hasValidSelection = computed(() => {
  return selectedItem.value !== null &&
         selectedItem.value !== undefined &&
         selectedItem.value.value !== null &&
         selectedItem.value.value !== undefined &&
         selectedItem.value.value !== ''
})

// Items are managed by the parent via :items prop and @search event
const filteredItems = computed(() => props.items)

// Handle focus
const onFocus = () => {
  if (props.showOptionsOnFocus) {
    showOptions.value = true
  }
}

// Reset search when item is selected
watch(selectedItem, (newValue) => {
  if (newValue) {
    nextTick(() => {
      showOptions.value = false
    })
  }
})

</script>